import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle, Sparkles, Award, ExternalLink, CheckCircle2 } from "lucide-react";
import { useCurrency } from "@/context/CurrencyContext";
import Money from "@/components/Money";

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d)) return null;
  return Math.round((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
};

export default function Certifications() {
  const [profile, setProfile] = useState(null);
  const [recs, setRecs] = useState(null);
  const [loadingRecs, setLoadingRecs] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", issuer: "", issue_date: "", expiry_date: "" });
  const [showAdd, setShowAdd] = useState(false);
  const { ready } = useCurrency();

  useEffect(() => {
    api.get("/profile").then(r => setProfile(r.data));
    api.get("/certifications/recommended").then(r => setRecs(r.data)).catch(() => {});
  }, []);

  const save = async (certs) => {
    setSaving(true);
    try { const { data } = await api.put("/profile", { certifications: certs }); setProfile(data); } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const add = () => {
    if (!form.name.trim()) return toast.error("Name required");
    const next = [...(profile.certifications||[]), { ...form, id: crypto.randomUUID?.() || String(Date.now()) }];
    save(next).then(() => { setForm({ name: "", issuer: "", issue_date: "", expiry_date: "" }); setShowAdd(false); toast.success("Certification added"); });
  };

  const remove = (id) => save((profile.certifications||[]).filter(c => c.id !== id));

  const genRecs = async () => {
    setLoadingRecs(true);
    try { const r = await api.post("/certifications/recommended/refresh"); setRecs(r.data); toast.success("Recommendations ready"); }
    catch { toast.error("Failed"); } finally { setLoadingRecs(false); }
  };

  if (!profile) return <div className="p-10">Loading...</div>;
  const certs = profile.certifications || [];

  return (
    <div className="p-10 max-w-5xl" data-testid="certifications-page">
      <div className="flex justify-between items-start mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">Module 07</div>
          <h1 className="font-display font-bold text-5xl tracking-tight">Certifications</h1>
          <p className="text-muted-foreground mt-2 text-sm">Track what you have. Get recommendations for what pays.</p>
        </div>
        <button data-testid="cert-add-btn" onClick={()=>setShowAdd(true)} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors px-4 py-2.5 text-sm flex items-center gap-2"><Plus size={14}/> Add certification</button>
      </div>

      {/* Current certs */}
      <div className="mb-10">
        <div className="font-display font-bold text-xl tracking-tight mb-4">Your certifications ({certs.length})</div>
        {certs.length === 0 ? (
          <div className="card-flat p-10 text-center text-muted-foreground">No certifications yet. Add some or upload a resume to auto-import.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {certs.map((c) => {
              const days = daysUntil(c.expiry_date);
              const expired = days !== null && days < 0;
              const expiringSoon = days !== null && days >= 0 && days <= 60;
              return (
                <div key={c.id} className="card-flat p-4" data-testid={`cert-${c.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <div className="font-display font-bold text-base">{c.name}</div>
                      <div className="text-xs text-muted-foreground">{c.issuer || "—"}</div>
                    </div>
                    <button onClick={()=>remove(c.id)} className="text-muted-foreground/60 hover:text-red-400"><Trash2 size={14}/></button>
                  </div>
                  <div className="mt-3 flex items-center gap-3 text-xs font-mono">
                    <span className="text-muted-foreground">Issued {c.issue_date || "—"}</span>
                    {c.expiry_date && <span className={expired ? "text-red-400" : expiringSoon ? "text-amber-400" : "text-muted-foreground"}>Expires {c.expiry_date}</span>}
                  </div>
                  {expired && <div className="mt-3 flex items-center gap-2 text-xs text-red-400"><AlertTriangle size={12}/> Expired — renew this ASAP</div>}
                  {!expired && expiringSoon && <div className="mt-3 flex items-center gap-2 text-xs text-amber-400"><AlertTriangle size={12}/> Renews in {days} days</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recommendations */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-display font-bold text-xl tracking-tight">AI recommendations</div>
            <div className="text-xs text-muted-foreground font-mono mt-1">Based on your profile · ranked by ROI</div>
          </div>
          <button data-testid="cert-generate" onClick={genRecs} disabled={loadingRecs} className="inline-flex items-center justify-center rounded-md border border-border bg-background hover:bg-accent text-foreground transition-colors px-4 py-2 text-sm flex items-center gap-2"><Sparkles size={14}/> {loadingRecs? "Analyzing..." : recs? "Refresh" : "Generate"}</button>
        </div>
        {!recs ? (
          <div className="card-flat p-10 text-center text-muted-foreground">Click Generate for personalized certification recommendations.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3" data-testid="cert-recs">
            {(Array.isArray(recs) ? recs : []).map((r, i) => (
              <div key={i} className="card-flat p-4" data-testid={`cert-rec-${i}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-display font-bold text-base">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.issuer}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display font-black text-lg text-emerald-400" data-testid={`cert-rec-lift-${i}`}>
                      {ready ? <>+<Money value={r.expected_salary_lift} from={r.currency || "USD"} /></> : "—"}
                    </div>
                    <div className="text-[10px] font-mono uppercase text-muted-foreground">est. lift</div>
                  </div>
                </div>
                <div className="text-sm text-foreground/80 mb-3">{r.why}</div>
                <div className="grid grid-cols-3 gap-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  <Meta label="Difficulty" value={r.difficulty}/>
                  <Meta label="Time" value={`${r.time_weeks}w`}/>
                  <Meta label="Cost" value={(r.cost_usd ?? r.cost) ? <Money value={r.cost_usd ?? r.cost} from={r.currency || "USD"} /> : "—"} testId={`cert-rec-cost-${i}`}/>
                </div>
                <div className="mt-2 text-[10px] font-mono uppercase tracking-widest">
                  <span className="text-muted-foreground">Demand:</span> <span className="text-foreground ml-1">{r.employer_demand}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="cert-add-modal">
          <div className="card-flat p-6 max-w-md w-full">
            <div className="font-display font-bold text-2xl tracking-tight mb-4">Add certification</div>
            <div className="space-y-3">
              {[["Name","name","text"],["Issuer","issuer","text"],["Issue date","issue_date","date"],["Expiry date","expiry_date","date"]].map(([label, key, type]) => (
                <div key={key}>
                  <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</label>
                  <input data-testid={`cert-form-${key}`} type={type} value={form[key]} onChange={e=>setForm({...form, [key]: e.target.value})} className="w-full mt-1 bg-card border border-border px-4 py-2"/>
                </div>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <button data-testid="cert-form-save" onClick={add} disabled={saving} className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-semibold transition-colors flex-1 py-2.5">Add</button>
              <button onClick={()=>setShowAdd(false)} className="inline-flex items-center justify-center rounded-md border border-border bg-background hover:bg-accent text-foreground transition-colors px-4">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Meta({ label, value, testId }) {
  return (
    <div className="border border-border p-2" data-testid={testId}>
      <div className="text-[9px]">{label}</div>
      <div className="text-foreground font-body font-medium text-xs mt-0.5">{value || "—"}</div>
    </div>
  );
}
